import React, { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { UserPlus, UserX, UserCheck, ShieldCheck, Info, Pencil, Search, Users, Radio } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import Modal from '../../components/admin/Modal';
import CriarUsuarioModal from '../../components/admin/CriarUsuarioModal';
import ConfirmarStatusUsuarioModal from '../../components/admin/ConfirmarStatusUsuarioModal';
import DetalhesUsuarioModal from '../../components/admin/DetalhesUsuarioModal';
import EditarUsuarioModal from '../../components/admin/EditarUsuarioModal';
import { useAdminAuth } from '../../components/admin/RequireAuth';
import { adminUsuariosAPI } from '../../utils/adminUsuariosAPI';

const BADGE_TIPO = {
  admin: 'bg-indigo-100 text-indigo-700',
  gerenciador: 'bg-blue-50 text-blue-700'
};

const MINUTOS_ONLINE = 5;

function estaOnline(usuario) {
  if (!usuario.ativo || !usuario.ultimo_acesso) return false;
  return Date.now() - new Date(usuario.ultimo_acesso).getTime() <= MINUTOS_ONLINE * 60 * 1000;
}

function tempoRelativo(dataStr) {
  const diffMin = Math.floor((Date.now() - new Date(dataStr).getTime()) / 60000);
  if (diffMin < 1) return 'agora mesmo';
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  return `há ${Math.floor(diffH / 24)}d`;
}

const Avatar = ({ usuario, tamanho = 'w-11 h-11 text-base' }) => (
  <div className="relative flex-shrink-0">
    <div className={`${tamanho} rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold overflow-hidden`}>
      {usuario.foto ? (
        <img src={adminUsuariosAPI.fotoUrl(usuario.id)} alt="" className="w-full h-full object-cover" />
      ) : (
        usuario.nome_completo?.[0]?.toUpperCase() || '?'
      )}
    </div>
    {estaOnline(usuario) && (
      <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white" title="Online agora" />
    )}
  </div>
);

const StatCard = ({ icon: Icone, cor, titulo, valor, delay }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 p-5 flex items-center gap-3.5 animate-fade-in-up"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cor}`}>
      <Icone className="w-5 h-5 text-white" />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-bold text-gray-900 leading-none">{valor}</p>
      <p className="text-xs font-medium text-gray-500 mt-1 leading-snug">{titulo}</p>
    </div>
  </div>
);

// Cada card fora do componente pai de propósito — definir isso dentro de
// GerenciarUsuarios recriaria o tipo do componente a cada render (cada
// tecla digitada na busca), remontando tudo e refazendo o fetch de cada
// <img> de foto.
const UsuarioCard = ({ usuario, ativoTab, sessao, podeEditar, onVerDetalhes, onEditar, onMudarStatus, delay }) => (
  <div
    className={`bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 p-5 flex flex-col gap-4 animate-fade-in-up ${ativoTab ? '' : 'opacity-70'}`}
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="flex items-start gap-3 min-w-0">
      <Avatar usuario={usuario} />
      <div className="min-w-0 flex-1">
        <h3 className={`text-gray-900 truncate ${ativoTab ? 'font-semibold' : 'font-medium'}`}>{usuario.nome_completo}</h3>
        <p className="text-sm text-gray-500 truncate">{usuario.email}</p>
        <div className="flex items-center gap-1.5 flex-wrap mt-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${BADGE_TIPO[usuario.tipo]}`}>
            {usuario.tipo === 'admin' && <ShieldCheck className="w-3 h-3" />} {usuario.tipo}
          </span>
          {usuario.id === sessao?.id && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">você</span>
          )}
          {ativoTab && usuario.ultimo_acesso && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${estaOnline(usuario) ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-400'}`}>
              {estaOnline(usuario) ? 'online agora' : `visto ${tempoRelativo(usuario.ultimo_acesso)}`}
            </span>
          )}
        </div>
      </div>
    </div>

    <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-gray-50">
      <button
        onClick={() => onVerDetalhes(usuario)}
        className="flex items-center gap-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-1.5 border border-gray-200"
      >
        <Info className="w-3.5 h-3.5" /> Detalhes
      </button>
      {podeEditar(usuario) && (
        <button
          onClick={() => onEditar(usuario)}
          className="flex items-center gap-1.5 text-sm text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg px-3 py-1.5 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" /> Editar
        </button>
      )}
      {sessao?.tipo === 'admin' && usuario.id !== sessao?.id && (
        ativoTab ? (
          <button
            onClick={() => onMudarStatus(usuario, 'desativar')}
            className="flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:bg-amber-50 rounded-lg px-3 py-1.5 transition-colors ml-auto"
          >
            <UserX className="w-3.5 h-3.5" /> Desativar
          </button>
        ) : (
          <button
            onClick={() => onMudarStatus(usuario, 'reativar')}
            className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-3 py-1.5 transition-colors ml-auto"
          >
            <UserCheck className="w-3.5 h-3.5" /> Reativar
          </button>
        )
      )}
    </div>
  </div>
);

const GerenciarUsuarios = () => {
  const sessao = useAdminAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarCriar, setMostrarCriar] = useState(false);
  const [modalStatus, setModalStatus] = useState(null); // { usuario, acao: 'desativar'|'reativar' }
  const [usuarioDetalhes, setUsuarioDetalhes] = useState(null);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [aba, setAba] = useState('ativos'); // 'ativos'|'desativados'
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos'); // 'todos'|'admin'|'gerenciador'

  const carregar = async () => {
    setLoading(true);
    try {
      setUsuarios(await adminUsuariosAPI.listar());
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  // MySQL retorna BOOLEAN como número (0/1) — comparar com === true/false
  // falha por checagem estrita de tipo do JS.
  const aplicarFiltros = (lista) => lista.filter((u) => {
    if (filtroTipo !== 'todos' && u.tipo !== filtroTipo) return false;
    if (busca.trim()) {
      const alvo = `${u.nome_completo} ${u.email}`.toLowerCase();
      if (!alvo.includes(busca.trim().toLowerCase())) return false;
    }
    return true;
  });

  const usuariosAtivosTodos = usuarios.filter((u) => Boolean(u.ativo));
  const usuariosAtivos = useMemo(() => aplicarFiltros(usuariosAtivosTodos), [usuarios, busca, filtroTipo]); // eslint-disable-line react-hooks/exhaustive-deps
  const usuariosDesativados = useMemo(() => aplicarFiltros(usuarios.filter((u) => !u.ativo)), [usuarios, busca, filtroTipo]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalAdmins = usuariosAtivosTodos.filter((u) => u.tipo === 'admin').length;
  const totalGerenciadores = usuariosAtivosTodos.filter((u) => u.tipo === 'gerenciador').length;
  const totalOnline = usuariosAtivosTodos.filter(estaOnline).length;

  const podeEditar = (usuario) => sessao?.tipo === 'admin' || usuario.id === sessao?.id;
  const onMudarStatus = (usuario, acao) => setModalStatus({ usuario, acao });

  const listaAtual = aba === 'ativos' ? usuariosAtivos : usuariosDesativados;

  return (
    <AdminLayout title="Usuários do painel">
      <Toaster position="top-center" />

      <div className="flex justify-between items-start gap-4 mb-6">
        <p className="text-gray-500 max-w-lg">Quem tem acesso ao painel administrativo, e com qual papel.</p>
        {sessao?.tipo === 'admin' && (
          <button onClick={() => setMostrarCriar(true)} className="btn-primary flex items-center gap-2 flex-shrink-0">
            <UserPlus className="w-4 h-4" /> Novo usuário
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} cor="bg-primary-600" titulo="Usuários ativos" valor={usuariosAtivosTodos.length} delay={0} />
        <StatCard icon={ShieldCheck} cor="bg-indigo-500" titulo="Administradores" valor={totalAdmins} delay={0.05} />
        <StatCard icon={Users} cor="bg-[#40A1E6]" titulo="Gerenciadores" valor={totalGerenciadores} delay={0.1} />
        <StatCard icon={Radio} cor="bg-emerald-500" titulo="Online agora" valor={totalOnline} delay={0.15} />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="inline-flex items-center gap-1 bg-gray-100 rounded-xl p-1 flex-shrink-0">
          <button
            onClick={() => setAba('ativos')}
            className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              aba === 'ativos' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Ativos
            <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{usuariosAtivos.length}</span>
          </button>
          <button
            onClick={() => setAba('desativados')}
            className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              aba === 'desativados' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Desativados
            <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{usuariosDesativados.length}</span>
          </button>
        </div>

        <div className="relative flex-1 min-w-[160px]">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou e-mail..."
            className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all"
          />
        </div>

        <select
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all flex-shrink-0"
        >
          <option value="todos">Todos os tipos</option>
          <option value="admin">Administrador</option>
          <option value="gerenciador">Gerenciador</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-spinner mx-auto" />
      ) : listaAtual.length === 0 ? (
        <p className="text-gray-500 text-center py-12">Nenhum usuário encontrado.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {listaAtual.map((usuario, index) => (
            <UsuarioCard
              key={usuario.id}
              usuario={usuario}
              ativoTab={aba === 'ativos'}
              sessao={sessao}
              podeEditar={podeEditar}
              onVerDetalhes={setUsuarioDetalhes}
              onEditar={setUsuarioEditando}
              onMudarStatus={onMudarStatus}
              delay={Math.min(index * 0.04, 0.4)}
            />
          ))}
        </div>
      )}

      {mostrarCriar && (
        <Modal titulo="Novo usuário" onFechar={() => setMostrarCriar(false)} maxWidth="max-w-md">
          <CriarUsuarioModal onFechar={() => setMostrarCriar(false)} onCriado={carregar} />
        </Modal>
      )}

      {modalStatus && (
        <Modal
          titulo={modalStatus.acao === 'desativar' ? 'Desativar usuário' : 'Reativar usuário'}
          onFechar={() => setModalStatus(null)}
          maxWidth="max-w-md"
        >
          <ConfirmarStatusUsuarioModal
            usuario={modalStatus.usuario}
            acao={modalStatus.acao}
            onFechar={() => setModalStatus(null)}
            onConfirmado={carregar}
          />
        </Modal>
      )}

      {usuarioDetalhes && (
        <Modal titulo="Detalhes do usuário" onFechar={() => setUsuarioDetalhes(null)} maxWidth="max-w-md">
          <DetalhesUsuarioModal usuario={usuarioDetalhes} usuarios={usuarios} onFechar={() => setUsuarioDetalhes(null)} />
        </Modal>
      )}

      {usuarioEditando && (
        <Modal titulo="Editar usuário" onFechar={() => setUsuarioEditando(null)} maxWidth="max-w-md">
          <EditarUsuarioModal
            usuario={usuarioEditando}
            sessaoAdmin={sessao}
            onFechar={() => setUsuarioEditando(null)}
            onAtualizado={carregar}
          />
        </Modal>
      )}
    </AdminLayout>
  );
};

export default GerenciarUsuarios;
